import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Clock,
  Plus,
  Send,
  Calendar,
  MapPin,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Edit,
  Trash2,
  FileText,
  RefreshCw,
} from 'lucide-react';

export default function MyTimesheetsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Selection for bulk submit
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    site_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in: '09:00',
    clock_out: '17:00',
    break_minutes: 0,
    notes: '',
    reason: '',
  });

  const fetchTimesheets = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/timesheets/my?start_date=${startDate}&end_date=${endDate}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const response = await api.get(url);
      // Handle both wrapped and unwrapped responses
      const data = response.data?.data || response.data || [];
      setTimesheets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate, statusFilter]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites?status=active&limit=200');
      // Handle both wrapped and unwrapped responses
      const data = response.data?.data || response.data || [];
      setSites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, [api]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get(`/timesheets/summary?start_date=${startDate}&end_date=${endDate}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [api, startDate, endDate]);

  useEffect(() => {
    fetchTimesheets();
    fetchSites();
    fetchSummary();
  }, [fetchTimesheets, fetchSites, fetchSummary]);

  const handleCreateTimesheet = async () => {
    if (!formData.site_id) {
      toast.error('Please select a site');
      return;
    }
    if (!formData.reason?.trim()) {
      toast.error('Please provide a reason for this manual entry');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        site_id: formData.site_id,
        date: formData.date,
        clock_in: `${formData.date}T${formData.clock_in}:00`,
        clock_out: formData.clock_out ? `${formData.date}T${formData.clock_out}:00` : null,
        break_minutes: parseInt(formData.break_minutes) || 0,
        notes: formData.notes,
        reason: formData.reason,
        source: 'manual',
      };

      await api.post('/timesheets', payload);
      toast.success('Timesheet created');
      setShowCreateDialog(false);
      resetForm();
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      console.error('Error creating timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTimesheet = async () => {
    if (!editingTimesheet) return;

    try {
      setSubmitting(true);
      const payload = {
        clock_in: formData.clock_in ? `${editingTimesheet.date}T${formData.clock_in}:00` : null,
        clock_out: formData.clock_out ? `${editingTimesheet.date}T${formData.clock_out}:00` : null,
        break_minutes: parseInt(formData.break_minutes) || 0,
        notes: formData.notes,
      };

      await api.put(`/timesheets/${editingTimesheet.id}`, payload);
      toast.success('Timesheet updated');
      setShowEditDialog(false);
      setEditingTimesheet(null);
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      console.error('Error updating timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to update timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTimesheet = async (id) => {
    if (!confirm('Are you sure you want to delete this timesheet?')) return;

    try {
      await api.delete(`/timesheets/${id}`);
      toast.success('Timesheet deleted');
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete timesheet');
    }
  };

  const handleSubmitTimesheet = async (id) => {
    try {
      await api.post(`/timesheets/${id}/submit`);
      toast.success('Timesheet submitted for approval');
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select timesheets to submit');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post('/timesheets/bulk-submit', {
        timesheet_ids: Array.from(selectedIds),
      });
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} timesheets submitted`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} failed: ${response.data.errors?.join(', ')}`);
      }
      
      setSelectedIds(new Set());
      fetchTimesheets();
      fetchSummary();
    } catch (error) {
      console.error('Error bulk submitting:', error);
      toast.error('Failed to submit timesheets');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      site_id: '',
      date: new Date().toISOString().split('T')[0],
      clock_in: '09:00',
      clock_out: '17:00',
      break_minutes: 0,
      notes: '',
      reason: '',
    });
  };

  const openEditDialog = (ts) => {
    setEditingTimesheet(ts);
    setFormData({
      site_id: ts.site_id,
      date: ts.date,
      clock_in: ts.clock_in ? new Date(ts.clock_in).toTimeString().slice(0, 5) : '09:00',
      clock_out: ts.clock_out ? new Date(ts.clock_out).toTimeString().slice(0, 5) : '17:00',
      break_minutes: ts.break_minutes || 0,
      notes: ts.notes || '',
      reason: ts.reason || '',
    });
    setShowEditDialog(true);
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAllDrafts = () => {
    const draftIds = timesheets.filter(ts => ts.status === 'draft' && ts.clock_out).map(ts => ts.id);
    setSelectedIds(new Set(draftIds));
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
      submitted: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      paid: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.draft}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const formatTime = (isoString) => {
    if (!isoString) return '—';
    try {
      // Use 24-hour format consistently
      return new Date(isoString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
      return '—';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Parse YYYY-MM-DD as local date to avoid timezone shift
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const localDate = new Date(year, month, day);
        return localDate.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
      }
      return new Date(dateStr).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="my-timesheets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white">My Timesheets</h1>
          <p className="text-zinc-400 text-sm">View and submit your worked hours</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-blue-500 hover:bg-blue-600"
          data-testid="create-timesheet-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Manual Entry
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-[#1a1a1d] border-[#3f3f46]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Total Hours</p>
                  <p className="text-lg font-bold text-white">{summary.total_hours?.toFixed(1) || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1d] border-[#3f3f46]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <Clock className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Overtime</p>
                  <p className="text-lg font-bold text-white">{summary.overtime_hours?.toFixed(1) || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1d] border-[#3f3f46]">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-xs text-zinc-500">Draft</p>
                <p className="text-lg font-bold text-zinc-400">{summary.by_status?.draft || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1d] border-[#3f3f46]">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-xs text-zinc-500">Submitted</p>
                <p className="text-lg font-bold text-amber-400">{summary.by_status?.submitted || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1d] border-[#3f3f46]">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-xs text-zinc-500">Approved</p>
                <p className="text-lg font-bold text-emerald-400">{summary.by_status?.approved || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] w-[150px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] w-[150px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-[#09090b] border-[#3f3f46]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchTimesheets(); fetchSummary(); }}
              className="border-zinc-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {selectedIds.size > 0 && (
              <Button
                onClick={handleBulkSubmit}
                disabled={submitting}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Selected ({selectedIds.size})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timesheets List */}
      <Card className="bg-[#1a1a1d] border-[#3f3f46]">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="font-rajdhani text-lg">Timesheet Entries</CardTitle>
          {timesheets.some(ts => ts.status === 'draft' && ts.clock_out) && (
            <Button variant="ghost" size="sm" onClick={selectAllDrafts} className="text-xs">
              Select All Drafts
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : timesheets.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No timesheets found for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#3f3f46]">
                    <th className="text-left py-3 px-2 text-zinc-400 font-medium w-10"></th>
                    <th className="text-left py-3 px-2 text-zinc-400 font-medium">Date</th>
                    <th className="text-left py-3 px-2 text-zinc-400 font-medium">Site</th>
                    <th className="text-center py-3 px-2 text-zinc-400 font-medium">Clock In</th>
                    <th className="text-center py-3 px-2 text-zinc-400 font-medium">Clock Out</th>
                    <th className="text-center py-3 px-2 text-zinc-400 font-medium">Break</th>
                    <th className="text-right py-3 px-2 text-zinc-400 font-medium">Hours</th>
                    <th className="text-right py-3 px-2 text-zinc-400 font-medium">OT</th>
                    <th className="text-center py-3 px-2 text-zinc-400 font-medium">Status</th>
                    <th className="text-center py-3 px-2 text-zinc-400 font-medium">Source</th>
                    <th className="text-right py-3 px-2 text-zinc-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheets.map((ts) => (
                    <tr key={ts.id} className="border-b border-[#3f3f46]/50 hover:bg-[#27272a]">
                      <td className="py-3 px-2">
                        {ts.status === 'draft' && ts.clock_out && (
                          <Checkbox
                            checked={selectedIds.has(ts.id)}
                            onCheckedChange={() => toggleSelection(ts.id)}
                          />
                        )}
                      </td>
                      <td className="py-3 px-2 text-white">{formatDate(ts.date)}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-zinc-500" />
                          <span className="text-zinc-300">{ts.site_name || '—'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center text-zinc-300">{formatTime(ts.clock_in)}</td>
                      <td className="py-3 px-2 text-center text-zinc-300">{formatTime(ts.clock_out)}</td>
                      <td className="py-3 px-2 text-center text-zinc-400">{ts.break_minutes || 0}m</td>
                      <td className="py-3 px-2 text-right font-medium text-white">{ts.total_hours?.toFixed(1) || '—'}</td>
                      <td className="py-3 px-2 text-right">
                        {ts.overtime_hours > 0 ? (
                          <span className="text-orange-400">{ts.overtime_hours.toFixed(1)}</span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">{getStatusBadge(ts.status)}</td>
                      <td className="py-3 px-2 text-center">
                        <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-500">
                          {ts.source === 'manual' ? 'Manual' : ts.source === 'roster_auto' ? 'Roster' : 'Route'}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {ts.status === 'draft' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(ts)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4 text-zinc-400" />
                              </Button>
                              {ts.clock_out && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSubmitTimesheet(ts.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Send className="w-4 h-4 text-emerald-400" />
                                </Button>
                              )}
                              {ts.source === 'manual' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteTimesheet(ts.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              )}
                            </>
                          )}
                          {ts.status === 'rejected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(ts)}
                              className="h-8 w-8 p-0"
                              title={`Rejected: ${ts.rejected_reason}`}
                            >
                              <AlertCircle className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Manual Entry Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#1a1a1d] border-[#3f3f46] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani">Create Manual Timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Site *</Label>
              <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v})}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clock In *</Label>
                <Input
                  type="time"
                  value={formData.clock_in}
                  onChange={(e) => setFormData({...formData, clock_in: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock Out</Label>
                <Input
                  type="time"
                  value={formData.clock_out}
                  onChange={(e) => setFormData({...formData, clock_out: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Break (minutes)</Label>
              <Input
                type="number"
                min="0"
                value={formData.break_minutes}
                onChange={(e) => setFormData({...formData, break_minutes: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason for Manual Entry *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Explain why a manual entry is needed (e.g., no roster shift assigned, emergency coverage)"
                className="bg-[#09090b] border-[#3f3f46]"
                rows={2}
              />
              <p className="text-xs text-zinc-500">
                Manual entries are only allowed when no roster shift exists for this date.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
                className="bg-[#09090b] border-[#3f3f46]"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateTimesheet}
              disabled={submitting}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#1a1a1d] border-[#3f3f46] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani">Edit Timesheet</DialogTitle>
          </DialogHeader>
          {editingTimesheet && (
            <div className="space-y-4 py-4">
              <div className="bg-[#27272a] rounded-md p-3">
                <p className="text-sm text-zinc-400">
                  <span className="text-white font-medium">{editingTimesheet.site_name}</span>
                  <span className="mx-2">·</span>
                  {formatDate(editingTimesheet.date)}
                </p>
                {editingTimesheet.status === 'rejected' && editingTimesheet.rejected_reason && (
                  <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                    <p className="text-xs text-red-400">
                      <strong>Rejected:</strong> {editingTimesheet.rejected_reason}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Clock In</Label>
                  <Input
                    type="time"
                    value={formData.clock_in}
                    onChange={(e) => setFormData({...formData, clock_in: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clock Out</Label>
                  <Input
                    type="time"
                    value={formData.clock_out}
                    onChange={(e) => setFormData({...formData, clock_out: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Break (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.break_minutes}
                  onChange={(e) => setFormData({...formData, break_minutes: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-[#09090b] border-[#3f3f46]"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateTimesheet}
              disabled={submitting}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
